import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Flame } from "lucide-react";

/**
 * Delivery-density heatmap card for /admin/reports.
 *
 * Pulls /api/stats/reports/heatmap?range&pharmacy_id&driver_id, then renders a
 * red-graduated heat overlay on a Leaflet base map. Re-renders whenever any of
 * the upstream Reports filters change. Auto-fits the map to the bounding box
 * of all returned points so a busy NYC borough and a quiet TX suburb both
 * land centered on first paint.
 */
export default function DeliveryHeatmap({ range, customRange, pharmacyId, driverId }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const heatLayer = useRef(null);
  const [points, setPoints] = useState([]);
  const [meta, setMeta] = useState({ count: 0, unique_cells: 0, max_weight: 0 });
  const [loading, setLoading] = useState(false);

  // -- Init the Leaflet map once.
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    const m = L.map(mapRef.current, {
      zoomControl: true,
      scrollWheelZoom: false, // don't hijack scroll on a report page
    }).setView([39.0, -97.0], 4); // continental US default
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 18,
      attribution: '&copy; OpenStreetMap',
    }).addTo(m);
    mapInstance.current = m;
    return () => {
      m.remove();
      mapInstance.current = null;
    };
  }, []);

  // -- Fetch points whenever filters change.
  useEffect(() => {
    const params = { range };
    if (range === "custom" && customRange?.from) {
      const toIso = (d) => {
        const yr = d.getFullYear();
        const mo = String(d.getMonth() + 1).padStart(2, "0");
        const da = String(d.getDate()).padStart(2, "0");
        return `${yr}-${mo}-${da}`;
      };
      params.start = toIso(customRange.from);
      params.end = toIso(customRange.to || customRange.from);
    }
    if (pharmacyId && pharmacyId !== "all") params.pharmacy_id = pharmacyId;
    if (driverId && driverId !== "all") params.driver_id = driverId;
    setLoading(true);
    api
      .get("/stats/reports/heatmap", { params })
      .then((r) => {
        setPoints(r.data.points || []);
        setMeta({
          count: r.data.count || 0,
          unique_cells: r.data.unique_cells || 0,
          max_weight: r.data.max_weight || 0,
        });
      })
      .catch(() => {
        setPoints([]);
        setMeta({ count: 0, unique_cells: 0, max_weight: 0 });
      })
      .finally(() => setLoading(false));
  }, [range, customRange, pharmacyId, driverId]);

  // -- Re-render the heat layer whenever the points change.
  useEffect(() => {
    const m = mapInstance.current;
    if (!m) return;

    if (heatLayer.current) {
      m.removeLayer(heatLayer.current);
      heatLayer.current = null;
    }
    if (!points.length) return;

    // Red-graduated palette (light blush → deep crimson). Stops are normalized
    // 0→1; leaflet.heat picks the band based on `max` (set below) so even a
    // single delivery still renders visibly without saturating to black.
    const gradient = {
      0.0: "rgba(254, 226, 226, 0)", // transparent blush
      0.2: "#FCA5A5",                // soft red
      0.4: "#F87171",                // light red
      0.6: "#EF4444",                // medium red
      0.8: "#DC2626",                // deep red
      1.0: "#991B1B",                // crimson
    };

    heatLayer.current = L.heatLayer(points, {
      radius: 28,
      blur: 22,
      maxZoom: 14,
      minOpacity: 0.45,
      max: Math.max(meta.max_weight || 1, 3),
      gradient,
    }).addTo(m);

    // Fit map to the point bounding box on every refresh.
    const bounds = L.latLngBounds(points.map((p) => [p[0], p[1]]));
    if (bounds.isValid()) {
      m.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    }
  }, [points, meta.max_weight]);

  return (
    <Card className="p-4 mb-6 overflow-hidden" data-testid="reports-heatmap-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-base flex items-center gap-1.5">
            <Flame className="h-4 w-4 text-red-600" />
            Delivery density heatmap
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Red shades show areas with more deliveries — darker means more stops.
            Hotspots aggregate stops within ~110m grid cells.
          </p>
        </div>
        <div className="text-right text-[11px] text-muted-foreground" data-testid="reports-heatmap-meta">
          <div>
            <span className="font-semibold text-foreground" data-testid="reports-heatmap-count">{meta.count}</span> stops
          </div>
          <div>{meta.unique_cells} cells · peak {meta.max_weight}</div>
        </div>
      </div>

      <div className="relative rounded-lg overflow-hidden border border-border" style={{ height: 380 }}>
        <div ref={mapRef} className="w-full h-full" data-testid="reports-heatmap-map" />
        {loading && (
          <div className="absolute inset-0 bg-background/50 grid place-items-center text-xs text-muted-foreground pointer-events-none">
            Loading heatmap…
          </div>
        )}
        {!loading && !points.length && (
          <div className="absolute inset-0 grid place-items-center text-sm text-muted-foreground pointer-events-none">
            No deliveries in the selected range
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 mt-3 text-[11px] text-muted-foreground" data-testid="reports-heatmap-legend">
        <span>Fewer</span>
        <div
          className="flex-1 h-2 rounded-full"
          style={{
            background:
              "linear-gradient(to right, #FEE2E2, #FCA5A5, #F87171, #EF4444, #DC2626, #991B1B)",
          }}
        />
        <span>More stops</span>
      </div>
    </Card>
  );
}
